// AllAITasksTab.js
// Global view of all AI tasks across the system with 3-Brain analysis

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { TabsContent } from '../../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Progress } from '../../../components/ui/progress';
import { ScrollArea } from '../../../components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Separator } from '../../../components/ui/separator';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';
import { 
  Bot, Brain, Sparkles, Zap, FileText, FileSpreadsheet, Presentation,
  Download, Eye, RefreshCw, Clock, CheckCircle, AlertCircle, Play,
  ArrowRight, Users, ChevronDown, ChevronRight, ExternalLink, Loader2,
  Search, Filter, Calendar, Building2, User, FileIcon, Mail, 
  ClipboardList, RotateCcw, Wifi, WifiOff
} from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { aiWorkforceAPI, contactsAPI } from '../../../api';
import { useAITaskProgress } from '../../../hooks/useAITaskProgress';

// Real-time Progress Indicator Component
const RealTimeProgressIndicator = ({ taskId, taskProgress, stage }) => {
  const progress = taskProgress[taskId];
  
  if (!progress) return null;
  
  const getStageLabel = (stage) => {
    const labels = {
      'initializing': 'Initializing...',
      'analyzing': 'Analyzing requirements...',
      'searching_resources': 'Searching resources...',
      'left_brain': '🧠 Left Brain (GPT-4o) analyzing...',
      'left_brain_complete': '✓ Left Brain complete',
      'right_brain': '🎨 Right Brain (Claude) creating...',
      'right_brain_complete': '✓ Right Brain complete',
      'cortex': '⚡ Cortex (Gemini) synthesizing...',
      'cortex_complete': '✓ Cortex complete',
      'generating_deliverables': 'Generating deliverables...',
      'completed': '✅ Completed',
      'failed': '❌ Failed'
    };
    return labels[stage] || stage;
  };
  
  const getBrainColor = (stage) => {
    if (stage?.includes('left_brain')) return 'bg-blue-500';
    if (stage?.includes('right_brain')) return 'bg-purple-500';
    if (stage?.includes('cortex')) return 'bg-emerald-500';
    return 'bg-gray-500';
  };
  
  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground animate-pulse">
          {getStageLabel(progress.stage)}
        </span>
        <span className="font-medium">{progress.progress}%</span>
      </div>
      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
        <div 
          className={`h-full transition-all duration-500 ${getBrainColor(progress.stage)}`}
          style={{ width: `${progress.progress}%` }}
        />
      </div>
    </div>
  );
};

// Brain Analysis Card Component
const BrainAnalysisCard = ({ title, icon: Icon, color, assessment, isLoading }) => (
  <Card className={`border-l-4 ${color}`}>
    <CardHeader className="pb-2">
      <CardTitle className="text-sm flex items-center gap-2">
        <Icon className="h-4 w-4" />
        {title}
        {assessment?.confidence_score && (
          <Badge variant="outline" className="ml-auto text-xs">
            {Math.round(assessment.confidence_score)}% confidence
          </Badge>
        )}
      </CardTitle>
    </CardHeader>
    <CardContent className="pt-0">
      {isLoading ? (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Processing...</span>
        </div>
      ) : assessment ? (
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground line-clamp-3">
            {assessment.assessment?.substring(0, 300)}...
          </p>
          {assessment.key_points?.length > 0 && (
            <div className="space-y-1">
              {assessment.key_points.slice(0, 3).map((point, idx) => (
                <div key={idx} className="flex items-start gap-2 text-xs">
                  <CheckCircle className="h-3 w-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Not yet processed</p>
      )}
    </CardContent>
  </Card>
);

// Deliverable Item Component
const DeliverableItem = ({ deliverable, onView, onDownload }) => {
  const getIcon = () => {
    switch (deliverable.type) {
      case 'word': return FileText;
      case 'excel': return FileSpreadsheet;
      case 'powerpoint': return Presentation;
      case 'pdf': return FileIcon;
      case 'email': return Mail;
      default: return FileText;
    }
  };
  const Icon = getIcon();
  
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
      <div className="flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${
          deliverable.type === 'word' ? 'bg-blue-100 text-blue-600' :
          deliverable.type === 'excel' ? 'bg-emerald-100 text-emerald-600' :
          deliverable.type === 'powerpoint' ? 'bg-orange-100 text-orange-600' :
          deliverable.type === 'email' ? 'bg-violet-100 text-violet-600' :
          'bg-slate-100 text-slate-600'
        }`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="font-medium text-sm">{deliverable.title || deliverable.name}</p>
          <p className="text-xs text-muted-foreground">
            {deliverable.type?.toUpperCase()} • {deliverable.created_at ? format(new Date(deliverable.created_at), 'MMM d, yyyy') : 'Recent'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => onView(deliverable)}>
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDownload(deliverable)}>
          <Download className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// AI Task Row Component
const AITaskRow = ({ task, onViewDetails, onViewDeliverables, onProcessTask, onGenerateDeliverables, isProcessing, isGenerating, taskProgress = {} }) => {
  const [expanded, setExpanded] = useState(false);
  const liveProgress = taskProgress[task.id];
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'in_progress': case 'processing': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'pending_review': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'failed': return 'bg-red-100 text-red-700 border-red-200';
      case 'pending': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <Card className={`overflow-hidden ${liveProgress ? 'ring-2 ring-purple-400 ring-opacity-50' : ''}`}>
      <div 
        className="p-4 cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
              liveProgress ? 'bg-gradient-to-br from-purple-500 to-violet-600 animate-pulse' : 'bg-gradient-to-br from-purple-500 to-violet-600'
            }`}>
              <Bot className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-medium truncate">{task.title}</p>
                {task.has_brain_analysis && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 text-xs">
                    <Brain className="h-3 w-3 mr-1" /> 3-Brain
                  </Badge>
                )}
                {liveProgress && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 text-xs animate-pulse">
                    <Wifi className="h-3 w-3 mr-1" /> Live
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <Bot className="h-3 w-3" />
                  {task.assigned_agent_name || task.agent_type || 'AI Agent'}
                </span>
                {task.contact_name && (
                  <span className="flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {task.contact_name}
                  </span>
                )}
                {task.company_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {task.company_name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4">
            <Badge className={getStatusColor(task.status)}>
              {task.status?.replace('_', ' ')}
            </Badge>
            {expanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )}
          </div>
        </div>
        
        {/* Real-time Progress Bar */}
        {liveProgress && (
          <RealTimeProgressIndicator 
            taskId={task.id} 
            taskProgress={taskProgress} 
            stage={liveProgress.stage} 
          />
        )}
        
        {/* Static Progress Bar for processing tasks without live updates */}
        {!liveProgress && (task.status === 'in_progress' || task.status === 'processing') && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
              <span>Processing with 3-Brain Analysis...</span>
              <span>{task.progress || 0}%</span>
            </div>
            <Progress value={task.progress || 0} className="h-1" />
          </div>
        )}
      </div>
      
      {expanded && (
        <div className="border-t px-4 py-3 bg-slate-50/50 space-y-3">
          {/* Task Description */}
          {task.description && (
            <p className="text-sm text-muted-foreground">{task.description}</p>
          )}
          
          {/* Quick Stats */}
          <div className="grid grid-cols-4 gap-2 text-center">
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-purple-600">{task.deliverables_count || 0}</p>
              <p className="text-xs text-muted-foreground">Deliverables</p>
            </div>
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-blue-600">{task.collaborators?.length || 0}</p>
              <p className="text-xs text-muted-foreground">Collaborators</p>
            </div>
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-emerald-600">
                {task.total_processing_time_ms ? `${(task.total_processing_time_ms / 1000).toFixed(1)}s` : '-'}
              </p>
              <p className="text-xs text-muted-foreground">Process Time</p>
            </div>
            <div className="p-2 bg-white rounded-lg">
              <p className="text-lg font-semibold text-amber-600">
                {task.brain_analyses_count || (task.reasoning ? 3 : 0)}
              </p>
              <p className="text-xs text-muted-foreground">Brain Analyses</p>
            </div>
          </div>
          
          {/* Source Info */}
          {(task.source_module || task.contact_id) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ClipboardList className="h-3 w-3" />
              <span>Source: {task.source_module || 'CRM'}</span>
              {task.contact_id && <span>• Contact linked</span>}
            </div>
          )}
          
          {/* Actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {task.status === 'pending' && (
              <Button 
                variant="default" 
                size="sm" 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={(e) => { e.stopPropagation(); onProcessTask(task); }}
                disabled={isProcessing}
                data-testid={`process-task-${task.id}`}
              >
                {isProcessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Play className="h-4 w-4 mr-2" />
                )}
                Process with 3-Brain
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              className="flex-1"
              onClick={(e) => { e.stopPropagation(); onViewDetails(task); }}
              data-testid={`view-analysis-${task.id}`}
            >
              <Brain className="h-4 w-4 mr-2" />
              View Analysis
            </Button>
            {(task.deliverables_count > 0 || task.deliverable_ids?.length > 0) && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1"
                onClick={(e) => { e.stopPropagation(); onViewDeliverables(task); }}
                data-testid={`view-deliverables-${task.id}`}
              >
                <FileText className="h-4 w-4 mr-2" />
                Deliverables
              </Button>
            )}
            {task.status === 'completed' && (
              <Button 
                variant="outline" 
                size="sm" 
                className="flex-1 border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                onClick={(e) => { e.stopPropagation(); onGenerateDeliverables(task); }}
                disabled={isGenerating}
                data-testid={`generate-deliverables-${task.id}`}
              >
                {isGenerating ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4 mr-2" />
                )}
                Generate Docs
              </Button>
            )}
          </div>
          
          {/* Timestamps */}
          <div className="flex items-center justify-between text-xs text-muted-foreground pt-2 border-t">
            <span>Created: {format(new Date(task.created_at), 'MMM d, yyyy h:mm a')}</span>
            {task.completed_at && (
              <span>Completed: {format(new Date(task.completed_at), 'MMM d, yyyy h:mm a')}</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

// Task Details Dialog with 3-Brain Analysis
const TaskDetailsDialog = ({ open, onOpenChange, task }) => {
  if (!task) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-purple-600" />
            AI Task Analysis: {task.title}
          </DialogTitle>
          <DialogDescription>
            Multi-brain analysis processed by {task.assigned_agent_name || task.agent_type || 'AI Agent'}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* 3-Brain Analysis Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <BrainAnalysisCard
              title="Left Brain (GPT-4o)"
              icon={Zap}
              color="border-l-blue-500"
              assessment={task.reasoning?.left_brain}
              isLoading={task.status === 'processing' && !task.reasoning?.left_brain}
            />
            <BrainAnalysisCard
              title="Right Brain (Claude)"
              icon={Sparkles}
              color="border-l-purple-500"
              assessment={task.reasoning?.right_brain}
              isLoading={task.status === 'processing' && !task.reasoning?.right_brain}
            />
            <BrainAnalysisCard
              title="Cortex (Gemini)"
              icon={Brain}
              color="border-l-emerald-500"
              assessment={task.reasoning?.cortex}
              isLoading={task.status === 'processing' && !task.reasoning?.cortex}
            />
          </div>
          
          {/* Synthesized Output */}
          {task.reasoning?.final_output && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500" />
                  Synthesized Output
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm whitespace-pre-wrap">{task.reasoning.final_output}</p>
                
                {task.reasoning.final_recommendations?.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-medium mb-2">Recommendations:</p>
                    <ul className="space-y-1">
                      {task.reasoning.final_recommendations.map((rec, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm">
                          <ArrowRight className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                          <span>{rec}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* AI Collaborators */}
          {task.collaborators?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  AI Collaborators Consulted ({task.collaborators.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-2">
                  {task.collaborators.map((collab, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Bot className="h-4 w-4 text-purple-600" />
                        <span className="text-sm font-medium">{collab.agent_name || collab.agent_type}</span>
                      </div>
                      <Badge variant="outline" className="text-xs">{collab.purpose || 'Consultation'}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Task not processed message */}
          {!task.reasoning && task.status === 'pending' && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Clock className="h-8 w-8 text-amber-500" />
                  <div>
                    <p className="font-medium text-amber-800">Task Not Yet Processed</p>
                    <p className="text-sm text-amber-600">
                      Click "Process with 3-Brain" to run the multi-brain analysis on this task.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Deliverables Dialog
const DeliverablesDialog = ({ open, onOpenChange, task, deliverables, onDownload }) => {
  if (!task) return null;
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Deliverables: {task.title}
          </DialogTitle>
          <DialogDescription>
            Generated documents and outputs from this AI task
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {deliverables?.length > 0 ? (
            deliverables.map(deliverable => (
              <DeliverableItem
                key={deliverable.id}
                deliverable={deliverable}
                onView={() => window.open(deliverable.url || deliverable.file_url, '_blank')}
                onDownload={() => onDownload(deliverable)}
              />
            ))
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No deliverables generated yet</p>
              <p className="text-xs mt-1">Process this task to generate deliverables</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Bulk Deliverable Dialog
const BulkDeliverableDialog = ({ 
  open, 
  onOpenChange, 
  task, 
  selectedTypes, 
  onTypesChange, 
  onGenerate, 
  isGenerating 
}) => {
  const deliverableOptions = [
    { id: 'word', label: 'Word Document', icon: FileText, color: 'text-blue-600' },
    { id: 'excel', label: 'Excel Spreadsheet', icon: FileSpreadsheet, color: 'text-emerald-600' },
    { id: 'powerpoint', label: 'PowerPoint', icon: Presentation, color: 'text-orange-600' },
    { id: 'pdf', label: 'PDF Document', icon: FileIcon, color: 'text-red-600' },
  ];

  const toggleType = (typeId) => {
    if (selectedTypes.includes(typeId)) {
      onTypesChange(selectedTypes.filter(t => t !== typeId));
    } else {
      onTypesChange([...selectedTypes, typeId]);
    }
  };

  if (!task) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            Generate Bulk Deliverables
          </DialogTitle>
          <DialogDescription>
            Select document types to generate for "{task.title?.substring(0, 40)}..."
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-3">
            {deliverableOptions.map(option => (
              <div 
                key={option.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedTypes.includes(option.id) 
                    ? 'border-emerald-500 bg-emerald-50' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => toggleType(option.id)}
              >
                <Checkbox 
                  checked={selectedTypes.includes(option.id)}
                  onCheckedChange={() => toggleType(option.id)}
                />
                <option.icon className={`h-5 w-5 ${option.color}`} />
                <Label className="flex-1 cursor-pointer">{option.label}</Label>
              </div>
            ))}
          </div>

          {selectedTypes.length === 0 && (
            <p className="text-sm text-amber-600 flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              Please select at least one document type
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={onGenerate}
            disabled={isGenerating || selectedTypes.length === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isGenerating ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            Generate {selectedTypes.length} Document{selectedTypes.length !== 1 ? 's' : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * All AI Tasks Tab - Global view of all AI tasks across the system
 */
export const AllAITasksTab = ({ 
  allTasks = [],
  onRefresh,
  isLoading = false 
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('all');
  const [selectedTask, setSelectedTask] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showDeliverablesDialog, setShowDeliverablesDialog] = useState(false);
  const [showBulkDeliverableDialog, setShowBulkDeliverableDialog] = useState(false);
  
  // Real-time WebSocket connection for task progress
  const { connected: wsConnected, taskProgress, lastUpdate, clearProgress } = useAITaskProgress('dashboard');
  const [deliverables, setDeliverables] = useState([]);
  const [processingTaskId, setProcessingTaskId] = useState(null);
  const [generatingTaskId, setGeneratingTaskId] = useState(null);
  const [selectedDeliverableTypes, setSelectedDeliverableTypes] = useState(['word', 'excel']);

  // Filter only AI-assigned tasks
  const aiTasks = useMemo(() => 
    allTasks.filter(t => t.assigned_to_type === 'ai_agent' || t.assigned_agent_id),
    [allTasks]
  );

  // Get unique agents for filter
  const uniqueAgents = useMemo(() => {
    const agents = new Map();
    aiTasks.forEach(t => {
      if (t.assigned_agent_name && !agents.has(t.assigned_agent_name)) {
        agents.set(t.assigned_agent_name, t.assigned_agent_id);
      }
    });
    return Array.from(agents.entries());
  }, [aiTasks]);

  // Filtered tasks
  const filteredTasks = useMemo(() => {
    return aiTasks.filter(task => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          task.title?.toLowerCase().includes(query) ||
          task.description?.toLowerCase().includes(query) ||
          task.assigned_agent_name?.toLowerCase().includes(query) ||
          task.contact_name?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && task.status !== statusFilter) return false;
      
      // Agent filter
      if (agentFilter !== 'all' && task.assigned_agent_name !== agentFilter) return false;
      
      return true;
    });
  }, [aiTasks, searchQuery, statusFilter, agentFilter]);

  // Stats
  const stats = useMemo(() => ({
    total: aiTasks.length,
    completed: aiTasks.filter(t => t.status === 'completed').length,
    inProgress: aiTasks.filter(t => ['in_progress', 'processing'].includes(t.status)).length,
    pending: aiTasks.filter(t => t.status === 'pending').length,
    failed: aiTasks.filter(t => t.status === 'failed').length
  }), [aiTasks]);

  const handleViewDetails = useCallback(async (task) => {
    setSelectedTask(task);
    setShowDetailsDialog(true);
    
    // Fetch reasoning if not loaded
    if (!task.reasoning && task.id) {
      try {
        const res = await aiWorkforceAPI.getTaskReasoning(task.id);
        setSelectedTask(prev => ({ ...prev, reasoning: res.data }));
      } catch (error) {
        console.error('Failed to fetch reasoning:', error);
      }
    }
  }, []);

  const handleViewDeliverables = useCallback(async (task) => {
    setSelectedTask(task);
    try {
      const res = await aiWorkforceAPI.getTaskDeliverables(task.id);
      setDeliverables(res.data.deliverables || res.data || []);
      setShowDeliverablesDialog(true);
    } catch (error) {
      toast.error('Failed to load deliverables');
    }
  }, []);

  const handleProcessTask = useCallback(async (task) => {
    setProcessingTaskId(task.id);
    try {
      toast.info('Starting 3-Brain Analysis...', { duration: 2000 });
      
      // Call the execute endpoint
      const res = await aiWorkforceAPI.executeTask(task.id);
      
      toast.success('Task processing started! The 3-Brain analysis is running.');
      
      // Refresh the task list
      if (onRefresh) {
        setTimeout(() => onRefresh(), 2000);
      }
    } catch (error) {
      console.error('Failed to process task:', error);
      toast.error(error.response?.data?.detail || 'Failed to start task processing');
    } finally {
      setProcessingTaskId(null);
    }
  }, [onRefresh]);

  const handleDownloadDeliverable = useCallback(async (deliverable) => {
    try {
      if (deliverable.url || deliverable.file_url) {
        window.open(deliverable.url || deliverable.file_url, '_blank');
        return;
      }
      
      let response;
      switch (deliverable.type) {
        case 'excel':
          response = await aiWorkforceAPI.exportToExcel(deliverable.id);
          break;
        case 'word':
          response = await aiWorkforceAPI.exportToWord(deliverable.id);
          break;
        default:
          toast.error('Download not available');
          return;
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${deliverable.title || 'deliverable'}.${deliverable.type === 'excel' ? 'xlsx' : 'docx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Download started');
    } catch (error) {
      toast.error('Failed to download');
    }
  }, []);

  const handleOpenBulkDeliverableDialog = useCallback((task) => {
    setSelectedTask(task);
    setSelectedDeliverableTypes(['word', 'excel']);
    setShowBulkDeliverableDialog(true);
  }, []);

  const handleGenerateBulkDeliverables = useCallback(async () => {
    if (!selectedTask || selectedDeliverableTypes.length === 0) {
      toast.error('Please select at least one deliverable type');
      return;
    }
    
    setGeneratingTaskId(selectedTask.id);
    try {
      toast.info(`Generating ${selectedDeliverableTypes.length} deliverables...`);
      
      const res = await aiWorkforceAPI.generateBulkDeliverables(selectedTask.id, selectedDeliverableTypes);
      
      const successCount = res.data.deliverables?.filter(d => d.status === 'completed').length || 0;
      if (successCount > 0) {
        toast.success(`Successfully generated ${successCount} deliverables!`);
      } else {
        toast.warning('Some deliverables failed to generate');
      }
      
      setShowBulkDeliverableDialog(false);
      
      // Refresh task list
      if (onRefresh) {
        setTimeout(() => onRefresh(), 1000);
      }
    } catch (error) {
      console.error('Failed to generate deliverables:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate deliverables');
    } finally {
      setGeneratingTaskId(null);
    }
  }, [selectedTask, selectedDeliverableTypes, onRefresh]);

  const handleProcessAllPending = useCallback(async () => {
    const pendingTasks = filteredTasks.filter(t => t.status === 'pending');
    if (pendingTasks.length === 0) {
      toast.info('No pending tasks to process');
      return;
    }
    
    toast.info(`Processing ${pendingTasks.length} pending tasks...`);
    
    for (const task of pendingTasks) {
      try {
        await aiWorkforceAPI.executeTask(task.id);
      } catch (error) {
        console.error(`Failed to process task ${task.id}:`, error);
      }
    }
    
    toast.success(`Started processing ${pendingTasks.length} tasks`);
    if (onRefresh) {
      setTimeout(() => onRefresh(), 3000);
    }
  }, [filteredTasks, onRefresh]);

  return (
    <TabsContent value="all-ai-tasks" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-6 w-6 text-purple-600" />
            All AI Tasks
            {/* WebSocket Connection Status */}
            <Badge variant="outline" className={`ml-2 text-xs ${wsConnected ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              {wsConnected ? (
                <><Wifi className="h-3 w-3 mr-1" /> Live</>
              ) : (
                <><WifiOff className="h-3 w-3 mr-1" /> Offline</>
              )}
            </Badge>
          </h2>
          <p className="text-muted-foreground">
            Global view of all AI-assigned tasks with 3-Brain analysis
            {Object.keys(taskProgress).length > 0 && (
              <span className="ml-2 text-purple-600 animate-pulse">
                • {Object.keys(taskProgress).length} task(s) processing
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={onRefresh}
            disabled={isLoading}
          >
            <RotateCcw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          {stats.pending > 0 && (
            <Button 
              onClick={handleProcessAllPending}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="process-all-pending-btn"
            >
              <Play className="h-4 w-4 mr-2" />
              Process All Pending ({stats.pending})
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-5 gap-4">
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-purple-500' : ''}`}
          onClick={() => setStatusFilter('all')}
        >
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-purple-600">{stats.total}</p>
            <p className="text-sm text-muted-foreground">Total AI Tasks</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'completed' ? 'ring-2 ring-emerald-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'completed' ? 'all' : 'completed')}
        >
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-600">{stats.completed}</p>
            <p className="text-sm text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'in_progress' ? 'ring-2 ring-blue-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'in_progress' ? 'all' : 'in_progress')}
        >
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">{stats.inProgress}</p>
            <p className="text-sm text-muted-foreground">In Progress</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'pending' ? 'ring-2 ring-amber-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
        >
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-600">{stats.pending}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card 
          className={`cursor-pointer transition-all hover:shadow-md ${statusFilter === 'failed' ? 'ring-2 ring-red-500' : ''}`}
          onClick={() => setStatusFilter(statusFilter === 'failed' ? 'all' : 'failed')}
        >
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-red-600">{stats.failed}</p>
            <p className="text-sm text-muted-foreground">Failed</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search tasks, agents, contacts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="ai-tasks-search"
              />
            </div>
            <Select value={agentFilter} onValueChange={setAgentFilter}>
              <SelectTrigger className="w-48" data-testid="agent-filter">
                <SelectValue placeholder="Filter by Agent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Agents</SelectItem>
                {uniqueAgents.map(([name, id]) => (
                  <SelectItem key={id || name} value={name}>{name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(statusFilter !== 'all' || agentFilter !== 'all' || searchQuery) && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => { setStatusFilter('all'); setAgentFilter('all'); setSearchQuery(''); }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Task List */}
      <ScrollArea className="h-[600px]">
        <div className="space-y-3 pr-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
            </div>
          ) : filteredTasks.length > 0 ? (
            filteredTasks.map(task => (
              <AITaskRow
                key={task.id}
                task={task}
                onViewDetails={handleViewDetails}
                onViewDeliverables={handleViewDeliverables}
                onProcessTask={handleProcessTask}
                onGenerateDeliverables={handleOpenBulkDeliverableDialog}
                isProcessing={processingTaskId === task.id}
                isGenerating={generatingTaskId === task.id}
                taskProgress={taskProgress}
              />
            ))
          ) : (
            <div className="text-center py-12">
              <Bot className="h-16 w-16 text-purple-200 mx-auto mb-4" />
              <h3 className="font-medium text-lg mb-2">No AI Tasks Found</h3>
              <p className="text-muted-foreground text-sm">
                {searchQuery || statusFilter !== 'all' || agentFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Tasks assigned to AI agents will appear here'
                }
              </p>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Dialogs */}
      <TaskDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        task={selectedTask}
      />
      
      <DeliverablesDialog
        open={showDeliverablesDialog}
        onOpenChange={setShowDeliverablesDialog}
        task={selectedTask}
        deliverables={deliverables}
        onDownload={handleDownloadDeliverable}
      />
      
      <BulkDeliverableDialog
        open={showBulkDeliverableDialog}
        onOpenChange={setShowBulkDeliverableDialog}
        task={selectedTask}
        selectedTypes={selectedDeliverableTypes}
        onTypesChange={setSelectedDeliverableTypes}
        onGenerate={handleGenerateBulkDeliverables}
        isGenerating={generatingTaskId !== null}
      />
    </TabsContent>
  );
};

export default AllAITasksTab;
